import { Flame, Brain, Clock, Target, MessageCircle, ChevronRight, Home, Activity, BarChart3, Book, User } from "lucide-react";

/**
 * iPhone-style hero mockup shown on the landing page.
 * Pure presentation — no props, no state, hand-crafted demo data.
 */
export default function PhoneMockup() {
  return (
    <div className="relative">
      <div className="relative bg-[#1c1c1e] rounded-[44px] p-2.5 shadow-[0_30px_70px_-15px_rgba(20,20,40,0.45)] ring-1 ring-black/20">
        <div className="relative bg-white rounded-[34px] overflow-hidden">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 w-[110px] h-[28px] bg-black rounded-full" aria-hidden="true">
            <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#222]" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-emerald-400/80" />
          </div>

          <div className="pt-12 pb-2 px-4 bg-cream min-h-[640px] flex flex-col gap-3">
            <div className="flex items-start justify-between pt-2">
              <div>
                <p className="text-[11px] text-inkmid">Good morning,</p>
                <h3 className="font-heading text-[17px] leading-tight text-inkdark">Pista&apos;s Plan for Today <span className="ml-1">👋</span></h3>
              </div>
              <div className="w-10 h-10 rounded-full bg-coral/15 flex items-center justify-center text-2xl shrink-0">🧒🏽</div>
            </div>

            <div className="rounded-2xl bg-mint/15 border border-mint/30 px-3 py-2.5 flex items-center justify-between">
              <div>
                <p className="text-[12px] font-bold text-mint-dark">Great progress! 🎉</p>
                <p className="text-[10px] text-inkmid leading-tight mt-0.5">You&apos;re building strong skills every day.</p>
              </div>
              <div className="text-right shrink-0 ml-2">
                <p className="text-[16px] font-heading text-inkdark leading-none">12 <Flame className="w-3.5 h-3.5 inline text-orange-500" fill="currentColor" /></p>
                <p className="text-[9px] text-inkmid leading-tight">Day streak</p>
              </div>
            </div>

            <div className="rounded-2xl bg-white shadow-soft border border-plum/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-bold text-inkdark">Development Snapshot</p>
                <p className="text-[9px] text-inkmid">Updated 2 days ago</p>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { icon: MessageCircle, ring: "bg-mint/15 text-mint", label: "Communication", lvl: "Level 3", sub: "Uses few\nmeaningful words" },
                  { icon: Brain, ring: "bg-plum/15 text-plum", label: "Understanding", lvl: "Level 4", sub: "Understands\ndaily routines" },
                  { icon: Clock, ring: "bg-coral/15 text-coral", label: "Attention", lvl: "Level 2", sub: "1–3 minutes" },
                ].map((d) => (
                  <div key={d.label} className="text-center">
                    <div className={`w-9 h-9 rounded-full mx-auto mb-1 flex items-center justify-center ${d.ring}`}>
                      <d.icon className="w-4.5 h-4.5" strokeWidth={2.4} />
                    </div>
                    <p className="text-[9px] font-bold leading-tight text-inkdark">{d.label}</p>
                    <p className="text-[10px] font-heading text-inkdark leading-tight">{d.lvl}</p>
                    <p className="text-[8px] text-inkmid leading-tight whitespace-pre-line">{d.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-plum/10 border border-plum/15 p-3 flex items-center justify-between">
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-coral/20 text-coral flex items-center justify-center shrink-0 mt-0.5">
                  <Target className="w-4 h-4" strokeWidth={2.3} />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-inkdark">Match Score</p>
                  <p className="text-[9px] text-inkmid leading-snug">Activities are a great match<br />for your child&apos;s current level.</p>
                </div>
              </div>
              <div className="relative w-12 h-12 shrink-0">
                <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E5E7EB" strokeWidth="3.5" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#34A853" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="92.5 100" pathLength="100" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-heading text-emerald-700">95%</span>
              </div>
            </div>

            <div className="mt-0.5">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-bold text-inkdark">Recommended for Today</p>
                <p className="text-[9px] text-inkmid">6 activities • ~40 min total</p>
              </div>
              <ul className="space-y-1.5">
                {[
                  { emoji: "🙋🏽‍♂️", title: "Simon Says", sub: "Listening & Following Instructions" },
                  { emoji: "🧷", title: "Clothespin Match", sub: "Fine Motor Skills" },
                  { emoji: "🦁", title: "Animal Action Game", sub: "Action Words" },
                ].map((a) => (
                  <li key={a.title} className="bg-white rounded-xl border border-plum/5 shadow-soft px-2.5 py-2 flex items-center gap-2">
                    <span className="text-xl shrink-0">{a.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-inkdark leading-tight truncate">{a.title}</p>
                      <p className="text-[9px] text-inkmid leading-tight truncate">{a.sub}</p>
                    </div>
                    <span className="text-[9px] font-bold text-emerald-700 bg-mint/15 rounded-full px-1.5 py-0.5">5 min</span>
                    <ChevronRight className="w-3.5 h-3.5 text-inkmid shrink-0" />
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex-1" />

            <div className="-mx-4 -mb-2 mt-1 bg-white border-t border-plum/10 px-2 pt-2 pb-3">
              <div className="grid grid-cols-5 text-center">
                {[
                  { icon: Home, label: "Today", active: true },
                  { icon: Activity, label: "Activities" },
                  { icon: BarChart3, label: "Progress" },
                  { icon: Book, label: "Resources" },
                  { icon: User, label: "Profile" },
                ].map((t) => (
                  <div key={t.label} className={`flex flex-col items-center gap-0.5 ${t.active ? "text-mint-dark" : "text-inkmid"}`}>
                    <t.icon className="w-4 h-4" strokeWidth={2.2} />
                    <span className="text-[9px] font-semibold">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
